import { useState, useEffect, useRef } from 'react';
import type { CustomerInfo, CountryOption, IntentType } from '../types';
import {
  validateEmail,
  validatePhone,
  getPhoneLengthHint,
  type EmailValidationResult,
  type PhoneValidationResult,
} from '../utils/validation';
import { t } from '../utils/translations';

interface ContactDrawerProps {
  isOpen: boolean;
  countries: CountryOption[];
  defaultCountry: string;
  onSubmit: (customer: CustomerInfo) => void;
  onDismiss: () => void;
  onOpen: () => void;
  triggerIntent?: IntentType | null;
  existingCustomer?: CustomerInfo;
  privacyPolicyUrl?: string;
  language?: string;
}

const INTENT_MESSAGES: Record<string, { message: string; focusField: 'email' | 'phone' }> = {
  callback_request: { message: 'Add your phone number to receive a callback', focusField: 'phone' },
  send_materials: { message: 'Where should we send the materials?', focusField: 'email' },
  send_solution: { message: "We'll email you the solution", focusField: 'email' },
  schedule_meeting: { message: 'Contact details to schedule your meeting', focusField: 'email' },
  request_quote: { message: 'Contact details for your quote request', focusField: 'email' },
  contact_sales: { message: 'Our sales team will contact you', focusField: 'email' },
  support_open_issue: { message: 'Contact details for your support request', focusField: 'email' },
};

// Contact icon SVG - small to fit in compact ear
const ContactIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

// Close icon SVG
const CloseIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

// Warning icon for validation errors
const WarningIcon = () => (
  <svg className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
  </svg>
);

export function ContactDrawer({
  isOpen,
  countries,
  defaultCountry,
  onSubmit,
  onDismiss,
  onOpen,
  triggerIntent,
  existingCustomer,
  privacyPolicyUrl = 'https://www.hw-group.com/privacy-policy',
  language = 'en',
}: ContactDrawerProps) {
  const [email, setEmail] = useState(existingCustomer?.email || '');
  const [name, setName] = useState(existingCustomer?.name || '');
  const [country, setCountry] = useState(existingCustomer?.country || defaultCountry);
  const [phone, setPhone] = useState(existingCustomer?.phone || '');
  const [acceptedTerms, setAcceptedTerms] = useState(true); // Default to checked
  const [formError, setFormError] = useState<string | null>(null);

  // Validation states
  const [emailValidation, setEmailValidation] = useState<EmailValidationResult | null>(null);
  const [phoneValidation, setPhoneValidation] = useState<PhoneValidationResult | null>(null);
  const [emailTouched, setEmailTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);

  const emailRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);

  // Get contextual message and focus field
  const intentConfig = triggerIntent ? INTENT_MESSAGES[triggerIntent] : null;
  const contextMessage = intentConfig?.message || t('contact.title', language);
  const focusField = intentConfig?.focusField || 'email';

  // Focus appropriate field when drawer opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        if (focusField === 'phone' && phoneRef.current) {
          phoneRef.current.focus();
        } else if (emailRef.current) {
          emailRef.current.focus();
        }
      }, 300); // Wait for slide animation
    }
  }, [isOpen, focusField]);

  // Pre-fill from existingCustomer when it changes
  useEffect(() => {
    if (existingCustomer) {
      if (existingCustomer.email) setEmail(existingCustomer.email);
      if (existingCustomer.name) setName(existingCustomer.name);
      if (existingCustomer.country) setCountry(existingCustomer.country);
      if (existingCustomer.phone) setPhone(existingCustomer.phone);
    }
  }, [existingCustomer]);

  // Reset validation when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setEmailTouched(false);
      setPhoneTouched(false);
      setEmailValidation(null);
      setPhoneValidation(null);
      setFormError(null);
    }
  }, [isOpen]);

  const selectedCountry = countries.find((c) => c.code === country);
  const phonePrefix = selectedCountry?.phonePrefix || '+1';
  const phoneLengthHint = getPhoneLengthHint(country);

  // Validate email on blur or change after touched
  const handleEmailBlur = () => {
    setEmailTouched(true);
    if (email) {
      setEmailValidation(validateEmail(email));
    }
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (formError) setFormError(null);
    if (emailTouched && value) {
      setEmailValidation(validateEmail(value));
    } else if (!value) {
      setEmailValidation(null);
    }
  };

  // Apply email suggestion
  const applySuggestion = () => {
    if (emailValidation?.suggestion) {
      setEmail(emailValidation.suggestion);
      setEmailValidation(validateEmail(emailValidation.suggestion));
    }
  };

  // Validate phone on blur or change after touched
  const handlePhoneBlur = () => {
    setPhoneTouched(true);
    if (phone) {
      setPhoneValidation(validatePhone(phone, country));
    }
  };

  const handlePhoneChange = (value: string) => {
    // Only allow digits, spaces, and dashes
    const cleanValue = value.replace(/[^\d\s-]/g, '');
    setPhone(cleanValue);
    if (phoneTouched && cleanValue) {
      setPhoneValidation(validatePhone(cleanValue, country));
    } else if (!cleanValue) {
      setPhoneValidation(null);
    }
  };

  // Re-validate phone when country changes
  useEffect(() => {
    if (phoneTouched && phone) {
      setPhoneValidation(validatePhone(phone, country));
    }
  }, [country, phone, phoneTouched]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate email
    const emailResult = validateEmail(email);
    setEmailValidation(emailResult);
    setEmailTouched(true);

    if (!emailResult.isValid) {
      setFormError(emailResult.error || 'Invalid email');
      return;
    }

    // Validate phone (if provided)
    if (phone) {
      const phoneResult = validatePhone(phone, country);
      setPhoneValidation(phoneResult);
      setPhoneTouched(true);

      if (!phoneResult.isValid) {
        setFormError(phoneResult.error || 'Invalid phone number');
        return;
      }
    }

    // Check terms
    if (!acceptedTerms) {
      setFormError(t('contact.privacyError', language));
      return;
    }

    // Format phone with prefix if provided
    const formattedPhone = phone ? `${phonePrefix}${phone.replace(/\D/g, '')}` : undefined;

    onSubmit({
      email: email.trim(),
      name: name.trim() || undefined,
      country,
      phone: formattedPhone,
      anonymous: false,
    });
  };

  // Show contact info badge if email is collected
  const hasEmail = existingCustomer?.email;

  // Input styling based on validation state
  const getInputClassName = (isValid: boolean | null, hasError: boolean) => {
    const base = 'w-full px-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-1';
    if (hasError) {
      return `${base} border-red-400 focus:ring-red-500 focus:border-red-500`;
    }
    if (isValid === true) {
      return `${base} border-green-400 focus:ring-green-500 focus:border-green-500`;
    }
    return `${base} border-gray-300 focus:ring-blue-500 focus:border-blue-500`;
  };

  const emailHasError = emailTouched && emailValidation && !emailValidation.isValid;
  const phoneHasError = phoneTouched && phoneValidation && !phoneValidation.isValid;

  return (
    <>
      {/* Icon handle - always visible on right edge, matching language ear design */}
      <button
        onClick={isOpen ? onDismiss : onOpen}
        className={`absolute right-0 bottom-20 z-40 flex items-center justify-center w-6 h-8 rounded-l-md shadow-md transition-all duration-200 hover:w-7 hover:shadow-lg ${
          isOpen
            ? 'bg-gray-200 text-gray-600'
            : hasEmail
              ? 'bg-green-500 text-white'
              : 'bg-white border border-r-0 border-gray-200 text-gray-600 hover:text-gray-800'
        }`}
        aria-label={isOpen ? 'Close contact form' : 'Open contact form'}
        title={isOpen ? 'Close' : hasEmail ? 'Contact info saved' : 'Add contact info'}
      >
        {isOpen ? <CloseIcon /> : hasEmail ? (
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        ) : <ContactIcon />}
      </button>

      {/* Compact sliding drawer from right - positioned above input area */}
      <div
        className={`absolute bottom-14 right-0 w-64 bg-white border border-gray-200 rounded-l-lg shadow-xl transform transition-transform duration-300 ease-out z-30 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
          <h3 className="text-xs font-medium text-gray-900">
            {contextMessage}
          </h3>
          <button
            onClick={onDismiss}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Compact Form */}
        <form onSubmit={handleSubmit} className="p-3 space-y-2" noValidate>
          {/* Email field with validation */}
          <div>
            <input
              ref={emailRef}
              type="email"
              value={email}
              onChange={(e) => handleEmailChange(e.target.value)}
              onBlur={handleEmailBlur}
              placeholder={`${t('contact.email', language)} *`}
              className={getInputClassName(
                emailTouched && emailValidation?.isValid ? true : null,
                !!emailHasError
              )}
              required
              aria-invalid={emailHasError ? 'true' : 'false'}
            />
            {/* Email validation feedback */}
            {emailHasError && emailValidation && (
              <div className="mt-1 flex items-start gap-1">
                <WarningIcon />
                <div className="flex-1">
                  <p className="text-[10px] text-amber-600">{emailValidation.error}</p>
                  {emailValidation.suggestion && (
                    <button
                      type="button"
                      onClick={applySuggestion}
                      className="text-[10px] text-blue-600 hover:underline"
                    >
                      Use {emailValidation.suggestion}
                    </button>
                  )}
                  {emailValidation.isDisposable && (
                    <p className="text-[9px] text-gray-500">
                      Temporary email addresses are not accepted
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('contact.nameOptional', language)}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
          />

          <div className="flex gap-1">
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="w-20 px-1 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {countries.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code}
                </option>
              ))}
            </select>
            <div className="flex flex-1">
              <span className="inline-flex items-center px-1.5 py-1.5 text-xs text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l">
                {phonePrefix}
              </span>
              <input
                ref={phoneRef}
                type="tel"
                value={phone}
                onChange={(e) => handlePhoneChange(e.target.value)}
                onBlur={handlePhoneBlur}
                placeholder={phoneLengthHint ? `${t('contact.phone', language)} (${phoneLengthHint})` : t('contact.phone', language)}
                className={`flex-1 w-full px-2 py-1.5 text-sm border rounded-r focus:outline-none focus:ring-1 ${
                  phoneHasError
                    ? 'border-red-400 focus:ring-red-500'
                    : phoneTouched && phoneValidation?.isValid
                      ? 'border-green-400 focus:ring-green-500'
                      : 'border-gray-300 focus:ring-blue-500'
                }`}
                aria-invalid={phoneHasError ? 'true' : 'false'}
              />
            </div>
          </div>
          {/* Phone validation feedback */}
          {phoneHasError && phoneValidation && (
            <div className="flex items-center gap-1 -mt-1">
              <WarningIcon />
              <p className="text-[10px] text-amber-600">{phoneValidation.error}</p>
            </div>
          )}

          {/* GDPR Checkbox - compact */}
          <label className="flex items-start gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => { setAcceptedTerms(e.target.checked); if (formError) setFormError(null); }}
              className="mt-0.5 w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-[10px] text-gray-500 leading-tight">
              {t('contact.privacyConsent', language)}{' '}
              <a
                href={privacyPolicyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                {t('contact.privacyLink', language)}
              </a>
            </span>
          </label>

          {formError && (
            <p className="text-[10px] text-red-500">{formError}</p>
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={!acceptedTerms}
              className={`flex-1 px-2 py-1.5 text-xs font-medium text-white rounded transition-opacity ${
                acceptedTerms ? 'hover:opacity-90' : 'opacity-50 cursor-not-allowed'
              }`}
              style={{ backgroundColor: 'var(--color-primary-override, #2563eb)' }}
            >
              {t('contact.continue', language)}
            </button>
            <button
              type="button"
              onClick={onDismiss}
              className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700"
            >
              {t('contact.skip', language)}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
